import React, { useMemo } from 'react'

import { FormHelperText } from '@mui/material'
import { useField } from 'formik'

import { AllergiesFieldProps, Disease } from '../../../ana-toolbox'
import { LATIN_CHARACTERS_ERROR } from '../../utils/yup/latinValidation'
import { latinDiseasesSchema } from '../../utils/yup/yup'
import { ChecklistInput } from '../checklist-input/checklist-input'
import { Translate } from '../translate/translate'

type DiseaseName = string

export const AllergiesField = ({ diseaseType, options = [], label, error }: AllergiesFieldProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [field, _meta, fieldHelpers] = useField<Disease[]>('diseases')

  // This object is used to reduce the complexity when checking if a disease is custom or not.
  const optionsByName: Record<DiseaseName, Disease> = useMemo(
    () => Object.fromEntries(options.map((option) => [option.name, option])),
    [options]
  )

  const diseasesOfType = field.value?.filter((disease) => disease.type === diseaseType) || []
  const selectedValues = diseasesOfType.map((disease) => disease.name)
  const latinError = latinDiseasesSchema.isValidSync(diseasesOfType) ? undefined : LATIN_CHARACTERS_ERROR
  // Save this value to spread them when saving the disease field, it's needed to keep
  // the values when saving the ones received in the onChange callback.
  const otherDiseases = field.value?.filter((disease) => disease.type !== diseaseType) || []

  const handleChange = (values: string[]) => {
    const diseaseValues: Disease[] = values.map((value) => ({
      type: diseaseType,
      name: value,
      custom: !optionsByName[value],
      medications: [],
    }))

    fieldHelpers.setValue([...otherDiseases, ...diseaseValues])
  }

  return (
    <>
      <ChecklistInput
        selectedValues={selectedValues}
        label={label}
        options={options.map((disease) => ({ value: disease.name, label: `option.${disease.name}` }))}
        onChange={handleChange}
        allowCustomOptions
        error={latinError}
      />

      {error && (
        <FormHelperText error>
          <Translate>error</Translate>
        </FormHelperText>
      )}

      {latinError && (
        <FormHelperText error>
          <Translate>{latinError}</Translate>
        </FormHelperText>
      )}
    </>
  )
}
